/**
 * Industry Solutions page translations - English
 */

export default {
  hero: {
    title: 'Industry Solutions',
    subtitle: 'Business-aligned digital and intelligent solutions for finance, manufacturing, retail, professional services, healthcare, government, and more',
    imageAlt: 'Industry Solutions Background'
  },
  
  overview: {
    title: 'Product Overview'
  },
  
  industries: {
    finance: {
      title: 'Financial Industry Solutions',
      imageAlt: 'Financial Industry Solutions',
      painPoints: {
        title: 'Pain Points',
        content: 'Cybersecurity threats, strict compliance requirements, aging legacy platforms, and rising expectations for customer experience'
      },
      solution: {
        title: 'Solutions',
        content: 'Build a secure and agile financial foundation through financial cloud modernization, unified regulatory reporting, managed security services, and omni-channel customer platforms'
      },
      results: {
        title: 'Implementation Results',
        content: 'Support core system modernization and automated regulatory reporting while shortening launch cycles and improving service consistency'
      }
    },
    
    ticketAi: {
      title: 'Smart Ticket & AI Analytics Platform',
      imageAlt: 'Smart Ticket & AI Analytics Platform',
      ticketSystem: {
        title: 'Smart Ticket System',
        features: [
          'Automated ticket creation, assignment and priority sorting',
          'Multi-channel access (email, phone, chat, API)',
          'SLA management and real-time monitoring dashboard',
          'Custom workflows and business rule engine',
          'Knowledge base integration and auto-reply suggestions'
        ]
      },
      aiAnalytics: {
        title: 'AI Analytics & Data Integration',
        features: [
          'Natural language processing technology to parse ticket content',
          'Predictive analytics to identify potential issues and trends',
          'Multi-source data integration and visualization reports',
          'Custom KPI monitoring and anomaly alerts',
          'Open API for seamless integration with third-party systems'
        ]
      },
      results: {
        title: 'Implementation Results',
        content: 'Ticket processing efficiency increased by 65%, manual errors reduced by 40%, customer satisfaction improved by 35%, operating costs reduced by 28%'
      }
    },
    
    manufacturing: {
      title: 'Manufacturing Industry Solutions',
      imageAlt: 'Manufacturing Industry Solutions',
      painPoints: {
        title: 'Pain Points',
        content: 'Supply disruptions, low production efficiency, equipment downtime, quality fluctuations, and fragmented data'
      },
      solution: {
        title: 'Solutions',
        content: 'Build smart factory and digital supply chain capabilities around Industrial IoT, MES-ERP integration, supply chain dashboards, and shop-floor digital twins'
      },
      results: {
        title: 'Implementation Results',
        content: 'Improve production transparency and traceability, reduce downtime risk, and strengthen cross-site supply chain collaboration'
      }
    },

    retail: {
      title: 'Retail Industry Solutions',
      imageAlt: 'Retail Industry Solutions',
      painPoints: {
        title: 'Pain Points',
        content: 'Distributed service requests across countries and stores, store network, POS and endpoint failures affecting operations, inconsistent local response and store lifecycle processes'
      },
      solution: {
        title: 'Solutions',
        content: 'Provide APAC-wide store IT support through a unified service desk, local engineer network, store IT lifecycle management, and standardized data-driven operations'
      },
      results: {
        title: 'Implementation Results',
        content: 'Centralize support for hundreds of stores, improve response speed and service control, and build a stable, efficient, and traceable store IT operating model'
      }
    },
    
    healthcare: {
      title: 'Healthcare Industry Solutions',
      imageAlt: 'Healthcare Industry Solutions',
      painPoints: {
        title: 'Pain Points',
        content: 'Medical data privacy protection, difficult cross-institutional data sharing, telemedicine system stability requirements'
      },
      solution: {
        title: 'Solutions',
        content: 'Secure and controllable medical data storage and exchange platform with privacy-compliant system architecture'
      },
      results: {
        title: 'Implementation Results',
        content: 'Data exchange efficiency increased by 60%, patient information security compliance rate of 100%, telemedicine system stability of 99.9%'
      }
    },
    
    government: {
      title: 'Government & Public Sector Solutions',
      imageAlt: 'Government & Public Sector Solutions',
      painPoints: {
        title: 'Pain Points',
        content: 'High e-government system stability requirements, strict data security protection needs, low cross-department collaboration efficiency'
      },
      solution: {
        title: 'Solutions',
        content: 'Highly reliable IT infrastructure and security protection system supporting multi-department data sharing and business collaboration'
      },
      results: {
        title: 'Implementation Results',
        content: 'System failure rate reduced by 45%, cross-department business processing efficiency increased by 50%, data security compliance rate of 100%'
      }
    }
  },

  focusIndustries: {
    title: 'Industry-Specific Solutions',
    subtitle: 'We organize the core solution portfolio around six scenarios: finance, manufacturing, retail, healthcare, government, and smart ticketing with AI analytics.',
    keySolutionLabel: 'Key Solution',
    painPointsTitle: 'Industry Pain Points',
    solutionTitle: 'Solution Components',
    outcomeTitle: 'Delivery Value',
    items: [
      {
        title: 'Manufacturing',
        keySolution: 'Smart Factory & Digital Supply Chain',
        painPoints: 'Supply chain disruption, low production efficiency, equipment downtime, quality fluctuation, and data silos.',
        solutions: [
          'Industrial IoT Platform: Connect machinery and production lines through sensors for real-time data collection, predictive maintenance, and energy management to reduce unplanned downtime.',
          'MES Integration: Integrate Manufacturing Execution Systems with ERP to digitalize and visualize production workflows from order to finished goods, improving efficiency and traceability.',
          'Digital Supply Chain Dashboard: Build a unified data platform for end-to-end visibility across supplier management, inventory optimization, and logistics tracking.',
          'Shop-Floor Digital Twin: Create virtual models of production lines for simulation, process optimization, commissioning, and employee training.'
        ],
        outcome: 'Combine connected equipment, process orchestration, and supply chain visibility to reduce unplanned downtime and improve quality traceability across manufacturing sites.'
      },
      {
        title: 'Financial Services',
        keySolution: 'Secure, Compliant, and Digital Customer Experience',
        painPoints: 'Cybersecurity threats, strict compliance demands, legacy core systems, and higher customer experience expectations.',
        solutions: [
          'Financial Cloud & Core System Modernization: Securely migrate core banking and insurance systems to financial clouds to improve resilience, agility, and development velocity.',
          'Unified Regulatory Reporting Platform: Build an automated data platform to support reporting and compliance requirements from regulators such as the PBOC and CBIRC.',
          'Managed Security Services for Finance: Provide 24/7 security monitoring, threat intelligence, and incident response to protect customer data and transaction flows.',
          'Omni-Channel Banking Platform: Integrate mobile banking, online banking, and branch systems to deliver a seamless and consistent personalized customer experience.'
        ],
        outcome: 'Use cloud modernization, automated compliance workflows, and managed security operations to improve resilience, auditability, and omni-channel service quality.'
      },
      {
        title: 'Retail',
        keySolution: 'Multi-Country Store IT Operations Solution',
        painPoints: 'Large store networks across countries, scattered issue channels, device and network failures affecting trading, inconsistent opening, relocation and closure processes, and uneven local response capabilities.',
        solutions: [
          'Unified Service Desk: Centralize all store IT requests, dispatch, SLA tracking, escalation, and closure management.',
          'Local Engineer Network: Cover key APAC locations with remote diagnosis and onsite support for store networks, POS, endpoints, and peripherals.',
          'Store IT Lifecycle Management: Support new store openings, daily operations, relocations, upgrades, and closure recovery with standardized delivery checklists.',
          'Standardized and Data-Driven Operations: Track store assets, service history, fault types, and response data to support dashboards and continuous improvement.'
        ],
        outcome: 'Help retail enterprises manage hundreds of stores efficiently and build a stable, efficient, and controllable store IT operating system with less business disruption.'
      },
      {
        title: 'Healthcare',
        keySolution: 'Secure and Compliant Medical Data Storage & Exchange',
        painPoints: 'Strict medical data privacy requirements, difficult cross-institution data sharing, and high stability expectations for telemedicine systems.',
        solutions: [
          'Medical Data Storage and Exchange Platform: Build secure data exchange channels for cross-institution collaboration and information sharing.',
          'Privacy Protection and Access Control: Design role, institution, and scenario-based permissions, audit trails, and data masking mechanisms.',
          'Telemedicine Stability Assurance: Improve availability across network, endpoint, application, and monitoring layers.',
          'Compliant Architecture Design: Plan privacy-compliant system architecture and operational processes for medical data protection.'
        ],
        outcome: 'Improve medical data exchange efficiency and telemedicine stability while protecting patient information security and compliance.',
      },
      {
        title: 'Government & Public Sector',
        keySolution: 'Highly Reliable IT Infrastructure and Security Protection',
        painPoints: 'High stability requirements for e-government systems, strict data security demands, and low efficiency in cross-department collaboration.',
        solutions: [
          'Highly Reliable Infrastructure: Build stable compute, network, storage, and disaster recovery capabilities for critical public systems.',
          'Data Security Protection: Cover identity authentication, access control, security monitoring, log audit, and incident response.',
          'Cross-Department Data Sharing: Unify data standards, API specifications, and sharing workflows to support multi-department collaboration.',
          'Operations and Compliance Governance: Establish inspection, monitoring, incident response, and compliance loops to reduce operating risk.'
        ],
        outcome: 'Reduce system failure rates, improve cross-department processing efficiency, and raise data security compliance for stable public services.'
      },
      {
        title: 'Smart Ticketing & AI Analytics',
        keySolution: 'Automated Service Management and Intelligent Operations Analytics',
        painPoints: 'Scattered service channels, inefficient manual assignment, hard-to-monitor SLA performance, and limited trend prediction and multi-source analytics.',
        solutions: [
          'Smart Ticketing System: Support automated ticket creation, assignment, priority sorting, multi-channel intake, SLA management, and custom workflows.',
          'Knowledge Base and Auto-Reply Suggestions: Integrate FAQ, SOP, and historical ticket experience into the service process.',
          'AI Analytics: Use NLP to parse ticket content and predictive analytics to detect potential issues, trends, and anomalies.',
          'Data Integration and Visualization: Consolidate multi-source data with custom KPIs, anomaly alerts, open APIs, and third-party integrations.'
        ],
        outcome: 'Improve ticket handling efficiency, reduce manual errors, increase customer satisfaction, and lower operating costs with more visible service management.'
      }
    ]
  },

  productMatrix: {
    title: 'Expanded Product and Digital Capabilities',
    subtitle: 'Beyond the smart ticketing and AI analytics platform, we also deliver CRM solutions, marketing automation, mini programs, AI knowledge bases, and office intelligent agents for customer operations, service workflows, and internal collaboration.',
    modulesTitle: 'Core Modules',
    scenariosTitle: 'Typical Scenarios',
    noteTitle: 'Composable planning with phased delivery',
    note: 'These capabilities can be deployed independently or integrated with your website, CRM, ERP, ticketing system, email workflows, and internal operational processes. We tailor the implementation approach to your current systems, team structure, and business flow.',
    action: 'Contact Us for a Solution',
    items: [
      {
        title: 'CRM Software Development',
        tagline: 'Membership systems and consumer user operations',
        description: 'Custom development of a membership CRM for brand-side consumer user management, covering member profiles, segmentation, loyalty points, coupons, benefits, engagement touchpoints, and repeat-purchase analytics.',
        modules: [
          'Member profiles, tags, and segmentation',
          'Loyalty points, coupons, tiers, and benefits',
          'User behavior analytics and repeat-purchase operations'
        ],
        scenarios: [
          'Brand membership operations',
          'Consumer user engagement',
          'Campaign growth and repeat purchase'
        ]
      },
      {
        title: 'MA Marketing Automation',
        tagline: 'Automated engagement and conversion growth',
        description: 'Connect website forms, CRM, email, SMS, and enterprise IM to build a closed loop for lead scoring, automated segmentation, customer journeys, and conversion attribution.',
        modules: [
          'Lead scoring and tagging framework',
          'Automated journeys and trigger-based messaging',
          'Campaign attribution and conversion analysis'
        ],
        scenarios: [
          'Website lead nurturing',
          'Trade show and event follow-up',
          'Member reactivation and repeat-purchase operations'
        ]
      },
      {
        title: 'Mini Program Development',
        tagline: 'Lightweight service entry point',
        description: 'Custom mini program development for customer service, member engagement, appointment submission, ticket inquiry, and business processing, with integrated backend operations.',
        modules: [
          'Login, membership, and organizational permissions',
          'Forms, ticket workflows, and message center',
          'CRM, ERP, payment, and API integrations'
        ],
        scenarios: [
          'Customer service entrance',
          'Internal repair requests and approvals',
          'Member services and appointment handling'
        ]
      },
      {
        title: 'AI Knowledge Base',
        tagline: 'Knowledge capture and intelligent Q&A',
        description: 'Unify FAQs, SOPs, ticket experience, product material, and internal documentation with permission controls, multilingual retrieval, and conversational knowledge services.',
        modules: [
          'Document ingestion and version management',
          'Semantic search and answer generation',
          'Permission control and feedback loop'
        ],
        scenarios: [
          'Customer support knowledge assistance',
          'Internal operations knowledge management',
          'Regional training and best-practice reuse'
        ]
      },
      {
        title: 'Office Intelligent Agents',
        tagline: 'Office workflow automation',
        description: 'Build agent capabilities for email, approvals, meeting minutes, task distribution, daily and weekly reports, and knowledge lookup, with access to enterprise systems and workflows.',
        modules: [
          'Email, IM, calendar, and document connectivity',
          'Workflow triggers and task orchestration',
          'Data write-back, audit logs, and permission control'
        ],
        scenarios: [
          'Sales follow-up automation',
          'Service operations collaboration',
          'Management meetings and knowledge Q&A'
        ]
      }
    ]
  },
  
  coreFeatures: {
    title: 'Core Features',
    
    ticketManagement: {
      title: 'Smart Ticket Management',
      description: 'Full lifecycle ticket tracking, from creation to resolution with complete process management, supporting custom fields and statuses. Automatic classification and priority sorting to improve ticket processing efficiency by up to 65%.',
      benefits: [
        'Automated Routing',
        'SLA Timer',
        'Batch Operations',
        'Team Collaboration'
      ]
    },
    
    dataAnalytics: {
      title: 'Real-time Data Analytics',
      description: 'Intuitive data visualization dashboard for real-time monitoring of key performance indicators, helping teams make quick decisions. Through multi-dimensional data display, anomaly identification speed increased by 40%.',
      benefits: [
        'Custom Reports',
        'Trend Analysis',
        'Department Comparison',
        'Export Function'
      ]
    },
    
    aiAssistant: {
      title: 'AI Smart Assistant',
      description: 'Machine learning-based intelligent analysis engine that automatically identifies problem patterns and provides solution suggestions. Through natural language processing technology, ticket auto-classification accuracy reaches 92%.',
      benefits: [
        'Auto Classification',
        'Priority Prediction',
        'Solution Recommendations',
        'Sentiment Analysis'
      ]
    },
    
    dataIntegration: {
      title: 'Data Integration Capabilities',
      description: 'Seamlessly connect with existing enterprise systems to achieve bidirectional data flow and eliminate information silos. Supports over 200 API integrations with data synchronization latency below 100ms.',
      benefits: [
        'API Interface',
        'Third-party Integration',
        'Data Synchronization',
        'Security Authentication'
      ]
    }
  },
  
  cta: {
    title: 'Customized Smart Solutions for Your Industry',
    description: 'Our smart ticket system and AI analytics platform have been successfully applied in finance, manufacturing, healthcare and other industries, helping enterprises improve operational efficiency, reduce costs and make data-driven decisions. Contact us for exclusive consultation services',
    button: 'Get Industry-Specific Solutions'
  }
}
